import { useCallback, useEffect, useState } from "react";
import { Calculator, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PriceChart from "@/components/PriceChart";
import ReportDialog from "@/components/ReportDialog";
import { errMsg, useApi } from "@/lib/api";
import { eur, num } from "@/lib/format";
import { useMe } from "@/context/MeContext";

const EditGrade = ({ grade, onClose, onSaved }) => {
  const api = useApi();
  const [form, setForm] = useState(grade);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    try {
      await api.put(`/grades/${grade.key}`, { name: form.name, purity: form.purity, description: form.description, percent: parseFloat(String(form.percent).replace(",", ".")) });
      toast.success("Sorte gespeichert");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="border-slate-200 bg-white" data-testid="dialog-edit-grade">
        <DialogHeader><DialogTitle>Sorte bearbeiten</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Bezeichnung</Label><Input value={form.name} onChange={set("name")} data-testid="input-grade-name" /></div>
          <div><Label>Reinheit</Label><Input value={form.purity} onChange={set("purity")} data-testid="input-grade-purity" /></div>
          <div><Label>Beschreibung</Label><Input value={form.description} onChange={set("description")} data-testid="input-grade-description" /></div>
          <div><Label>Anteil vom Börsenkurs (%)</Label><Input value={form.percent} onChange={set("percent")} data-testid="input-grade-percent" /></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="btn-save-grade">Speichern</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Calc = ({ grades, focus }) => {
  const [key, setKey] = useState(grades[0]?.key);
  const [kg, setKg] = useState("100");
  useEffect(() => { if (focus) setKey(focus); }, [focus]);
  const g = grades.find((x) => x.key === key);
  const weight = parseFloat(kg.replace(",", ".")) || 0;
  return (
    <div className="copper-glow h-fit space-y-4 p-6" data-testid="calculator-panel">
      <div className="flex items-center gap-2"><Calculator className="h-4 w-4 text-orange-700" /><h3 className="text-base font-semibold">Erlösrechner</h3></div>
      <div className="space-y-3">
        <div>
          <Label className="text-xs text-slate-500">Sorte</Label>
          <Select value={key} onValueChange={setKey}>
            <SelectTrigger data-testid="select-calc-grade"><SelectValue /></SelectTrigger>
            <SelectContent>{grades.map((x) => <SelectItem key={x.key} value={x.key}>{x.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs text-slate-500">Gewicht (kg)</Label><Input value={kg} onChange={(e) => setKg(e.target.value)} inputMode="decimal" data-testid="input-calc-weight-kg" /></div>
      </div>
      <div className="border-t border-slate-200 pt-4">
        <div className="text-xs uppercase tracking-wider text-slate-500">Geschätzter Erlös</div>
        <div className="num text-4xl font-bold text-orange-700" data-testid="text-calc-total-payout">{eur((g?.price_eur_kg || 0) * weight)}</div>
        <div className="num mt-1 text-xs text-slate-500">{eur(g?.price_eur_kg, 3)}/kg × {num(weight, 1)} kg</div>
      </div>
    </div>
  );
};

export default function Kurse() {
  const api = useApi();
  const me = useMe();
  const [grades, setGrades] = useState(null);
  const [edit, setEdit] = useState(null);
  const [focus, setFocus] = useState(null);

  const load = useCallback(() => api.get("/grades").then((r) => setGrades(r.data)), [api]);
  useEffect(() => { load(); }, [load]);

  if (!grades) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div>;
  const focusGrade = grades.find((g) => g.key === focus);

  return (
    <div className="space-y-6" data-testid="categories-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Kurse & Sorten</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">Richtpreise je Reinheit, berechnet als Anteil vom aktuellen Börsenkurs. Die Prozentwerte sind Richtwerte und können vom Admin angepasst werden.</p>
        </div>
        <ReportDialog gradeKey={focus || "boerse"} />
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="panel overflow-x-auto p-5 lg:col-span-2">
          <table className="w-full min-w-[640px] text-sm" data-testid="table-grades">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="pb-3">Sorte</th><th className="pb-3">Reinheit</th><th className="pb-3 text-right">Anteil</th><th className="pb-3 text-right">€/kg</th><th className="pb-3 text-right">€/t</th>{me.role === "admin" && <th />}</tr>
            </thead>
            <tbody>
              {grades.map((g) => (
                <tr key={g.key} onClick={() => setFocus(g.key === focus ? null : g.key)} className={`cursor-pointer border-t border-slate-200 transition-colors hover:bg-orange-50/60 ${focus === g.key ? "bg-orange-500/[0.06]" : ""}`} data-testid={`row-category-${g.key}`}>
                  <td className="py-3"><div className="font-medium">{g.name}</div><div className="text-xs text-slate-500">{g.description}</div></td>
                  <td className="py-3 text-slate-500">{g.purity}</td>
                  <td className="num py-3 text-right text-slate-500">{num(g.percent, 0)} %</td>
                  <td className="num py-3 text-right font-semibold text-orange-700">{eur(g.price_eur_kg, 3)}</td>
                  <td className="num py-3 text-right text-slate-600">{eur(g.price_eur_kg * 1000, 0)}</td>
                  {me.role === "admin" && (
                    <td className="py-3 text-right">
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setEdit(g); }} data-testid={`btn-edit-grade-${g.key}`}><Pencil className="h-4 w-4" /></Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Calc grades={grades} focus={focus} />
      </div>
      <div className="panel p-5">
        <h3 className="mb-2 text-base font-semibold" data-testid="grade-chart-title">Kursverlauf {focusGrade ? `– ${focusGrade.name}` : "– Börsenkurs"} (€/kg)</h3>
        <p className="mb-3 text-xs text-slate-500">Zeile in der Tabelle anklicken, um den Verlauf einer Sorte zu sehen.</p>
        <PriceChart factor={focusGrade ? focusGrade.percent / 100 : 1} />
      </div>
      {edit && <EditGrade grade={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
